import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { RobotoFontFamily } from '@packages/ui/shared';
import colors from '@packages/core/styles/colors';

interface AutoSizeTextareaProps {
    id: string;
    className?: string;
    disabled?: boolean;
    onChange: (next: string) => void;
    onBlur?: () => void;
    placeholder?: string;
    required?: boolean;
    value: string;
    isLabel?: boolean;
}

const useStyles = createUseStyles({
    container: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        position: 'relative',
    },
    textarea: {
        display: 'block',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        border: 'none',
        outline: 'none',
        backgroundColor: 'inherit',
        resize: 'none',
        overflow: 'hidden',
        padding: 0,
        paddingRight: (props: { required: boolean }) => (props.required ? 7 : 0),
        margin: 0,
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        fontSize: 15,
        fontFamily: RobotoFontFamily.Regular,
        color: colors.textDarkPrimary,
        '&:disabled': {
            color: colors.textDarkSecondary,
            opacity: 0.7,
        },
    },
    placeholderOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: (props: { required: boolean }) => (props.required ? 7 : 0),
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        pointerEvents: 'none',
        color: colors.textDarkSecondary,
        fontSize: 14,
        opacity: 0.7,
        fontFamily: RobotoFontFamily.Regular,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
    span: {
        color: colors.redOne,
        fontSize: 15,
        position: 'absolute',
        right: 0,
        top: 0,
    },
    textWeightMedium: {
        fontWeight: 500,
    },
});

export function AutoSizeTextarea({
    id,
    isLabel = false,
    className,
    disabled,
    onChange,
    onBlur,
    placeholder,
    required = false,
    value,
}: AutoSizeTextareaProps) {
    const classes = useStyles({ required });
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const pointerDownRef = useRef(false);

    const handlePointerDownCapture = useCallback(() => {
        pointerDownRef.current = true;

        window.setTimeout(() => {
            pointerDownRef.current = false;
        }, 0);
    }, []);

    const handleFocus = useCallback(() => {
        const textareaCurrent = textareaRef.current;
        if (!textareaCurrent) {
            return;
        }

        if (pointerDownRef.current) {
            return;
        }

        const end = textareaCurrent.value.length;
        textareaCurrent.setSelectionRange(end, end);
    }, []);

    const resizeToFitContent = useCallback(() => {
        const textareaCurrent = textareaRef.current;
        if (!textareaCurrent) return;

        textareaCurrent.style.height = 'auto';
        const nextHeight = `${textareaCurrent.scrollHeight}px`;
        if (textareaCurrent.style.height !== nextHeight) {
            textareaCurrent.style.height = nextHeight;
        }
    }, []);

    useLayoutEffect(() => {
        resizeToFitContent();
    }, [resizeToFitContent, value]);

    useEffect(() => {
        const textareaCurrent = textareaRef.current;
        if (!textareaCurrent) {
            return;
        }

        resizeToFitContent();

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', resizeToFitContent);
            return () => window.removeEventListener('resize', resizeToFitContent);
        }

        const resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(resizeToFitContent);
        });

        resizeObserver.observe(textareaCurrent);
        return () => resizeObserver.disconnect();
    }, [resizeToFitContent]);

    const showPlaceholder = !value && placeholder;

    return (
        <div
            className={classes.container}
            onPointerDownCapture={handlePointerDownCapture}
            onMouseDownCapture={handlePointerDownCapture}
            onTouchStartCapture={handlePointerDownCapture}
        >
            <textarea
                id={id}
                className={`${classes.textarea} ${isLabel ? classes.textWeightMedium : ''} ${className ?? ''}`}
                disabled={disabled}
                ref={textareaRef}
                required={required}
                rows={1}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onFocus={handleFocus}
                onBlur={onBlur}
            />
            {showPlaceholder && <span className={classes.placeholderOverlay}>{placeholder}</span>}
            {isLabel && required && <span className={classes.span}>{' *'}</span>}
        </div>
    );
}
